<script lang="ts">
    import { untrack } from 'svelte';
    import { fade } from 'svelte/transition';
    import type { TileImage } from '@shared/types/types';
    import { getImageUrl } from '@tile/components/product-image/ImageUrlBuilder.ts';
    import type { ImageAspectRatio, ImageLoadingOptions } from '@tile/components/product-image/types.ts';

    interface Props {
        image: TileImage;
        imageLoadingOptions: ImageLoadingOptions;
        variationId: string;
        detailPageLink?: string;
        onClick?: (event: Event) => void;
        size?: { width: string; height: string };
        aspectRatio?: ImageAspectRatio;
        linkType?: 'image' | 'tile';
    }

    let {
        image,
        variationId,
        imageLoadingOptions,
        detailPageLink,
        onClick,
        size,
        aspectRatio,
        linkType = 'image',
    }: Props = $props();

    const imagePaddingTop = $derived.by(() => {
        if (!aspectRatio) return '100%';

        switch (aspectRatio) {
            case 'landscape':
            case 'landscape-two-column':
                return '69.5%';
            case 'portrait':
                return '143%';
            default:
                return '100%';
        }
    });

    const shouldCut = $derived.by(() => {
        if (!image.cuttable) return false;
        if (image.cuttable === 'PORTRAIT') {
            return aspectRatio === 'portrait';
        }
        if (image.cuttable === 'LANDSCAPE') {
            return aspectRatio === 'landscape' || aspectRatio === 'landscape-two-column';
        }
        if (image.cuttable === 'SQUARE') {
            return aspectRatio === 'quad';
        }
        return false;
    });

    let loadingError = $state(false);

    const useDimPreset = $derived(image.grayBackground === true);
    const webpSrc = $derived(getImageUrl(image.id ?? '', 'webp', aspectRatio, useDimPreset));
    const jpegSrc = $derived(getImageUrl(image.id ?? '', 'jpeg', aspectRatio, useDimPreset));
    const imgSrc = $derived(loadingError ? (image.fallback ?? '') : jpegSrc);

    /**
 *
 *
 */
    let lastVariationId = $state(untrack(() => variationId));
    $effect(() => {
        if (variationId !== lastVariationId) {
            loadingError = false;
            lastVariationId = variationId;
        }
    });
</script>

<div
    class="reptile-product-image"
    data-qa={`ftfind-product-image-${variationId}`}
    data-gray-background={image.grayBackground}
    data-cuttable={image.cuttable}
    style:width={size?.width}
    style:height={size?.height}
    style:padding-top={imagePaddingTop}
>
    {#if detailPageLink}
        <!-- svelte-ignore a11y_no_static_element_interactions -->
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <oc-link-v2
            class="reptile-product-image__frame"
            base64-href={btoa(detailPageLink)}
            onclick={onClick}
            data-link-type={linkType}
        >
            {#key variationId}
                <picture class="reptile-product-image__picture" in:fade>
                    {#if !loadingError}
                        <source srcset={webpSrc} type="image/webp" />
                        <source srcset={jpegSrc} type="image/jpeg" />
                    {/if}
                    <img
                        class="reptile-product-image__img"
                        class:reptile-product-image__img--cut={shouldCut}
                        src={imgSrc}
                        alt={image.alt ?? ''}
                        onerror={() => {
                            loadingError = true;
                        }}
                        loading={imageLoadingOptions.loading}
                        fetchpriority={imageLoadingOptions.fetchpriority}
                    />
                </picture>
            {/key}
        </oc-link-v2>
    {:else}
        <div class="reptile-product-image__frame">
            {#key variationId}
                <picture class="reptile-product-image__picture" in:fade>
                    {#if !loadingError}
                        <source srcset={webpSrc} type="image/webp" />
                        <source srcset={jpegSrc} type="image/jpeg" />
                    {/if}
                    <img
                        class="reptile-product-image__img"
                        class:reptile-product-image__img--cut={shouldCut}
                        src={imgSrc}
                        alt={image.alt ?? ''}
                        onerror={() => {
                            loadingError = true;
                        }}
                        loading={imageLoadingOptions.loading}
                        fetchpriority={imageLoadingOptions.fetchpriority}
                    />
                </picture>
            {/key}
        </div>
    {/if}
</div>

<style lang="scss">
    @use '@otto-ec/design-tokens/tokens';

    .reptile-product-image {
        position: relative;

        &__frame {
            user-select: none;

            position: absolute;
            inset: 0;

            display: block;

            width: 100%;
            border: 2px solid tokens.$oc-semantic-color-canvas-background;

            background-color: #f5f5f5;
        }

        &__picture {
            display: block;
            width: 100%;
            height: 100%;
        }

        &__img {
            display: block;

            width: 100%;
            height: 100%;

            object-fit: contain;
            object-position: center;

            &--cut {
                object-fit: cover;
            }
        }
    }
</style>
